import {
  NumericFormat,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format'
import store from 'stores/store'
import MaxAmountButton from '@components/shared/MaxAmountButton'
import WalletIcon from '@components/icons/WalletIcon'
import { useCallback, useState } from 'react'
import { floorToDecimal, formatCurrencyValue } from 'utils/numbers'
import Decimal from 'decimal.js'
import SearchModal from '@components/modals/SearchModal'
import { TokenMetadataItem } from 'types/token'
import { SizeButtons } from '@components/swap/SellInput'
import SwapTokenSelect from '@components/swap/SwapTokenSelect'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import { useTranslation } from 'next-i18next'
import RefetchBalancesButton from '@components/shared/RefetchBalancesButton'

const set = store.getState().set

const SIZE_VALUES = ['50', '100'] as const
type SizeValue = (typeof SIZE_VALUES)[number]

const MAX_DIGITS = 11
export const withValueLimit = (values: NumberFormatValues): boolean => {
  return values.floatValue
    ? values.floatValue.toFixed(0).length <= MAX_DIGITS
    : true
}

function setInputAmountFormValue(amountIn: string) {
  set((s) => {
    s.trade.limitForm.inputAmount = amountIn
  })
}

const LimitSellInput = ({
  max,
  decimals,
  tickDecimals,
  error,
  setError,
  lastPrice,
}: {
  max: string
  decimals: number
  tickDecimals: number
  error: string | undefined
  setError: (e: string) => void
  lastPrice: number
}) => {
  const { t } = useTranslation('trade')
  const inputAmount = store((s) => s.trade.limitForm.inputAmount)
  const price = store((s) => s.trade.limitForm.price)
  const inputToken = store((s) => s.swap.inputToken)
  const quoteMint = store((s) => s.market.quoteToken.mint)
  const [showSelectList, setShowSelectList] = useState(false)
  const { baseMintPrice, quoteMintPrice } = useMarketTokensWithPrices()

  const isBid = inputToken.mint === quoteMint

  const setInputAndOutputAmounts = useCallback(
    (value: string) => {
      setInputAmountFormValue(value)
      store
        .getState()
        .actions.setCalculatedLimitFormOutputAmount(
          value,
          String(price || lastPrice),
          tickDecimals,
        )
    },
    [lastPrice, price, tickDecimals],
  )

  const handleInputAmountChange = useCallback(
    (e: NumberFormatValues, info: SourceInfo) => {
      if (info.source !== 'event') return

      const value = e.value === '.' ? '0.' : e.value
      setInputAndOutputAmounts(value)
      if (error) {
        setError('')
      }
    },
    [error, setError, setInputAndOutputAmounts],
  )

  const handleSizePercentage = useCallback(
    (percentage: '' | SizeValue) => {
      const walletMaxDecimal = new Decimal(max)
      if (walletMaxDecimal.gt(0) && percentage) {
        let amount = walletMaxDecimal.mul(percentage).div(100)
        if (percentage !== '100') {
          amount = floorToDecimal(amount, decimals)
        }
        const value = amount.toFixed()
        setInputAndOutputAmounts(value)
      } else {
        setInputAmountFormValue('')
        set((s) => {
          s.trade.limitForm.outputAmount = ''
        })
      }
      if (error) {
        setError('')
      }
    },
    [decimals, error, setError, max, setInputAndOutputAmounts],
  )

  const handleSelect = useCallback(
    async (newInputMint: string, newInputToken?: TokenMetadataItem) => {
      void store
        .getState()
        .actions.setNewInputToken(newInputMint, newInputToken)

      setShowSelectList(false)
    },
    [],
  )

  return (
    <>
      <div className="grid grid-cols-2 rounded-t-xl">
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <p className="text-th-fgd-1">{t('limit.sell')}</p>
          {inputToken.mint ? (
            <div className="flex items-center space-x-2">
              <span className="text-xs">
                <MaxAmountButton
                  decimals={decimals}
                  label={<WalletIcon className="size-3" />}
                  onClick={() => handleSizePercentage('100')}
                  value={max}
                  disabled={max === '0'}
                />
              </span>
              <RefetchBalancesButton />
            </div>
          ) : null}
        </div>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <SwapTokenSelect
            imageUrl={inputToken.image_url}
            symbol={inputToken.symbol}
            showList={setShowSelectList}
          />
          <SizeButtons
            onClick={handleSizePercentage}
            showClear={!!inputAmount}
            sizeValues={SIZE_VALUES}
          />
        </div>
        <div className="col-span-2">
          <div className="flex items-center">
            <NumericFormat
              inputMode="decimal"
              lang="en"
              allowedDecimalSeparators={['.', ',']}
              thousandSeparator=","
              allowNegative={false}
              valueIsNumericString
              decimalScale={decimals || 6}
              name="amount"
              id="amount"
              className="box-border h-10 w-full bg-transparent font-mono text-xl text-th-fgd-1 focus:outline-none md:hover:bg-transparent"
              placeholder="0.00"
              value={inputAmount}
              onValueChange={handleInputAmountChange}
              isAllowed={withValueLimit}
            />
            <p className="font-mono text-xs text-th-fgd-4">
              {inputAmount
                ? isBid
                  ? formatCurrencyValue(
                      +(inputAmount?.replace(/,/g, '') || '0') * quoteMintPrice,
                    )
                  : formatCurrencyValue(
                      +inputAmount.replace(/,/g, '') * baseMintPrice,
                    )
                : '$0.00'}
            </p>
          </div>
        </div>
      </div>
      {showSelectList ? (
        <SearchModal
          isOpen={showSelectList}
          onClose={() => setShowSelectList(false)}
          onResultClick={handleSelect}
          type="inputToken"
        />
      ) : null}
    </>
  )
}

export default LimitSellInput
